import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Button, Table, Tooltip } from "antd";
import { CheckCircleOutlined, PlusCircleOutlined } from "@ant-design/icons";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { compareStrings } from "@util/string";
import { formatCnpj } from "@/util/cnpj";
import { exportTableToExcel, showTotalWithSelection, createHandleRow } from "@util/tableUtils";

import styles from "./_styles.less";

const UnidadesBaseTable = forwardRef(
  ({ dataSource, loading, title, scroll, onRow, onClick, itemsList, isCadastro = false, ...props }, ref) => {
    const divRef = useRef(null);
    const { t } = useTranslation();
    const [pageSize, setPageSize] = useState(10);
    const [scrollY, setScrollY] = useState(scroll?.y);

    const fireClick = (item) => (typeof onClick === "function" ? onClick(item) : undefined);

    const columns = [];

    if (!isCadastro) {
      columns.push({
        render: (record) => {
          const isAdded = itemsList?.includes(record.id) ?? false;
          return (
              <Button
                type="text"
                onClick={() => fireClick(record)}
                icon={
                  isAdded ? (
                    <CheckCircleOutlined style={{ fontSize: 15 }} />
                  ) : (
                    <PlusCircleOutlined style={{ fontSize: 15 }} />
                  )
                }
                disabled={isAdded}
              />
          );
        },
        width: 50,
        align: "center",
      });
    }
    columns.push(
      {
        title: t("Apelido"),
        dataIndex: "apelido",
        key: "apelido",
        width: 200,
        sorter: (a, b) => compareStrings(a.apelido, b.apelido),
        ellipsis: { showTitle: false }, 
        render: (text) => <span>{text || t("(não informado)")}</span>,
      },
      {
        title: t("Endereço"),
        dataIndex: "endereco",
        key: "endereco",
        width: 300,
        sorter: (a, b) => compareStrings(a.endereco, b.endereco),
        ellipsis: { showTitle: false }, 
        render: (text) => <span>{text || t("(não informado)")}</span>,
      },
      {
        title: t("Cidade"),
        dataIndex: "cidade",
        key: "cidade",
        width: 160,
        sorter: (a, b) => compareStrings(a.cidade, b.cidade),
        ellipsis: { showTitle: false }, 
        render: (text) => text || "-",
      },
      {
        title: t("Estado"),
        dataIndex: "estado",
        key: "estado",
        width: 100,
        sorter: (a, b) => compareStrings(a.estado, b.estado),
        ellipsis: { showTitle: false }, 
        render: (text) => text || "-",
      },
      {
        title: t("CEP"),
        dataIndex: "cep",
        key: "cep",
        width: 130,
        sorter: (a, b) => compareStrings(a.cep, b.cep),
        ellipsis: { showTitle: false }, 
        render: (text) => text || "-",
      },
      {
        title: t("CNPJ"),
        dataIndex: "cnpj",
        key: "cnpj",
        width: 160,
        sorter: (a, b) => compareStrings(a.cnpj, b.cnpj),
        ellipsis: { showTitle: false },
        render: (text) => (text ? formatCnpj(text) : "-"),
      }
    );
    useImperativeHandle(
      ref,
      () => ({
        download: () =>
          exportTableToExcel({
            columns,
            dataSource,
          }),
      }),
      [columns, dataSource]
    );

    useEffect(() => {
      const updateHeight = () => {
        const drawerHeaderHeight = 300;
        const newHeight = window.innerHeight - drawerHeaderHeight;
        setScrollY(newHeight);
      };

      updateHeight();
      window.addEventListener("resize", updateHeight);

      return () => window.removeEventListener("resize", updateHeight);
    }, []);

    return (
      <div ref={divRef} className={styles["table-wrapper"]}>
        <Table
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          title={title}
          pagination={{
            pageSize,
            showTotal: showTotalWithSelection(t),
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            onShowSizeChange: (_, size) => setPageSize(size),
          }}
          rowKey="id"
          scroll={{ x: "max-content", y: scrollY }}
          onRow={(record, rowIndex) => createHandleRow(record, rowIndex, onRow)}
          {...props}
        />
      </div>
    );
  }
);

UnidadesBaseTable.displayName = "UnidadesBaseTable";

UnidadesBaseTable.propTypes = {
  dataSource: PropTypes.array,
  loading: PropTypes.bool,
  title: PropTypes.func,
  scroll: PropTypes.object,
  onClick: PropTypes.func, 
  itemsList: PropTypes.array, 
  isCadastro: PropTypes.bool, 
};

export default UnidadesBaseTable;
